import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    MdListAlt, MdFilterList, MdSearch, MdClose, MdKeyboardArrowLeft,
    MdKeyboardArrowRight, MdKeyboardArrowDown, MdShoppingCart,
    MdDateRange, MdLink, MdAttachMoney, MdWarning, MdShoppingBasket
} from 'react-icons/md';

import { productsAPI } from 'services/api';
import { useAuth } from 'context/AuthContext';

// --- YARDIMCI FONKSİYONLAR ---
const formatCurrency = (value) => {
    return (Number(value) || 0).toLocaleString('tr-TR', {
        style: 'currency',
        currency: 'TRY',
    });
};

const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch (e) {
        return dateString;
    }
};

const PAGE_SIZE = 10;

// --- SKELETON LOADERS ---
const ListSkeleton = () => (
    <div className="flex flex-col gap-4 animate-pulse mt-6">
        <div className="h-16 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="h-32 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50" />
            ))}
        </div>
    </div>
);

const AdminStockSalesHistory = () => {
    // --- STATE (WORK-LOGIC BİREBİR) ---
    const [salesHistory, setSalesHistory] = useState([]);
    const [productList, setProductList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtreleme
    const [filters, setFilters] = useState({
        product_id: 'all',
        start_date: '',
        end_date: '',
    });
    const [showFilters, setShowFilters] = useState(false);

    // Sayfalama
    const [currentPage, setCurrentPage] = useState(1);

    const { logout } = useAuth();
    const handleAuthError = useCallback(() => {
        logout();
    }, [logout]);

    // --- API FETCH (İlk Veri Yükleme) ---
    useEffect(() => {
        let mounted = true;
        const fetchInitialData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [salesRes, productsRes] = await Promise.all([
                    productsAPI.getSalesHistory(),
                    productsAPI.getAll(),
                ]);

                if (mounted) {
                    if (salesRes.success) {
                        setSalesHistory(salesRes.sales_history || []);
                    } else {
                        throw new Error('Satış geçmişi yüklenemedi.');
                    }

                    if (productsRes.success) {
                        setProductList(productsRes.products || []);
                    } else {
                        throw new Error('Ürün listesi yüklenemedi.');
                    }
                }
            } catch (err) {
                console.error('Veri yükleme hatası:', err);
                if (String(err.message).includes('401') || String(err.message).includes('Unauthorized')) {
                    handleAuthError();
                    return;
                }
                if (mounted) setError(err.message || 'Veriler yüklenirken bir hata oluştu.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchInitialData();
        return () => { mounted = false; };
    }, [handleAuthError]);

    // --- FİLTRELEME & SAYFALAMA MANTIĞI ---
    const filteredSales = useMemo(() => {
        return salesHistory.filter(sale => {
            if (filters.product_id !== 'all' && String(sale.product_id) !== filters.product_id) {
                return false;
            }

            const saleDate = sale.created_at ? sale.created_at.split('T')[0] : '';

            if (filters.start_date && saleDate < filters.start_date) {
                return false;
            }
            if (filters.end_date && saleDate > filters.end_date) {
                return false;
            }
            return true;
        });
    }, [salesHistory, filters]);

    const totalPages = Math.max(1, Math.ceil(filteredSales.length / PAGE_SIZE));
    const currentPageItems = filteredSales.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilters({ product_id: 'all', start_date: '', end_date: '' });
        setCurrentPage(1);
    };

    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BİLGİ VE AKSİYON */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 flex-none">
                        <MdListAlt className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Ürün Satış Geçmişi</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Satışı gerçekleşen ürünlerin hareket dökümünü ve finans bağlarını inceleyin.
                        </p>
                    </div>
                </div>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center justify-center gap-2 h-11 px-6 rounded-xl text-sm font-bold transition-all w-full md:w-auto ${
                        showFilters
                            ? 'bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-navy-700'
                    }`}
                >
                    <MdFilterList className="text-lg" /> {showFilters ? 'Filtreleri Gizle' : 'Filtrele & Ara'}
                </button>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold flex items-center gap-2">
                    <MdWarning className="text-xl" /> {error}
                </div>
            )}

            {/* 2. FİLTRELEME ALANI (Kapalı Başlar) */}
            <div className={`transition-all duration-300 overflow-hidden ${showFilters ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="bg-white dark:bg-navy-800 p-5 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 mb-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

                        <div className="lg:col-span-2">
                            <label className="text-[10px] font-bold text-gray-400 uppercase ml-1 block mb-1">Ürüne Göre Filtrele</label>
                            <div className="relative">
                                <select name="product_id" value={filters.product_id} onChange={handleFilterChange} className="w-full h-11 px-4 appearance-none rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white cursor-pointer transition-all">
                                    <option value="all">Tüm Ürünler</option>
                                    {productList.map(product => (
                                        <option key={product.id} value={product.id}>{product.name}</option>
                                    ))}
                                </select>
                                <MdKeyboardArrowDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-lg" />
                            </div>
                        </div>

                        <div className="flex gap-2 lg:col-span-2">
                            <div className="flex-1">
                                <label className="text-[10px] font-bold text-gray-400 uppercase ml-1 block mb-1">Başlangıç Tarihi</label>
                                <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all" />
                            </div>
                            <div className="flex-1">
                                <label className="text-[10px] font-bold text-gray-400 uppercase ml-1 block mb-1">Bitiş Tarihi</label>
                                <input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} className="w-full h-11 px-4 rounded-xl border border-gray-200 bg-gray-50 text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all" />
                            </div>
                        </div>

                        <div className="lg:col-span-4 flex justify-end">
                            <button onClick={resetFilters} className="h-10 px-6 rounded-xl bg-red-50 text-red-500 font-bold text-sm hover:bg-red-500 hover:text-white dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500 dark:hover:text-white transition-all">
                                Filtreleri Temizle
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* 3. LİSTE ALANI */}
            <div className="bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">

                {loading ? <ListSkeleton /> : filteredSales.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 opacity-60">
                        <MdShoppingBasket className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                        <h5 className="text-xl font-bold text-navy-700 dark:text-white">Satış Kaydı Bulunamadı</h5>
                        <p className="text-sm font-medium text-gray-500 mt-1">Seçili filtrelere uygun bir ürün satışı bulunmuyor.</p>
                    </div>
                ) : (
                    <>
                        {/* Masaüstü Tablo Görünümü */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left">
                                <thead>
                                <tr className="border-b border-gray-100 dark:border-white/10 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                    <th className="py-4 pl-4 w-[30%]">Ürün</th>
                                    <th className="py-4">Satış Tarihi</th>
                                    <th className="py-4 text-center">Adet</th>
                                    <th className="py-4">Toplam Tutar</th>
                                    <th className="py-4 pr-4">İlişkili Finansal İşlem</th>
                                </tr>
                                </thead>
                                <tbody>
                                {currentPageItems.map(sale => (
                                    <tr key={sale.id} className="border-b border-gray-50 dark:border-white/5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors">
                                        <td className="py-4 pl-4">
                                            <div className="flex items-center gap-3">
                                                {/* YENİ: Bootstrap yerine %100 Tailwind renkleri (Yeşil) */}
                                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-500 dark:bg-green-500/10 dark:text-green-400 font-bold text-lg flex-none">
                                                    <MdShoppingCart />
                                                </div>
                                                <div>
                                                    <h6 className="text-sm font-bold text-navy-700 dark:text-white line-clamp-1">{sale.product?.name || 'Silinmiş Ürün'}</h6>
                                                    <p className="text-xs font-medium text-gray-500 mt-0.5">SKU: {sale.product?.sku || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                                            {formatDateTime(sale.created_at)}
                                        </td>
                                        <td className="py-4 text-center">
                                            <span className="text-lg font-black text-navy-700 dark:text-white">{sale.quantity_sold}</span>
                                        </td>
                                        <td className="py-4">
                                            {/* YENİ: bg-green-50 ile daha soft yeşil arkaplan */}
                                            <span className="text-sm font-black text-green-600 bg-green-50 dark:bg-green-500/10 dark:text-green-400 px-2 py-1 rounded-md">
                          +{formatCurrency(sale.total_price)}
                        </span>
                                        </td>
                                        <td className="py-4 pr-4">
                                            {sale.transaction ? (
                                                <div className="flex flex-col">
                                                    {/* YENİ: text-info yerine Tailwind Mavi rengi eklendi */}
                                                    <span className="text-sm font-bold text-blue-500 dark:text-blue-400 flex items-center gap-1 line-clamp-1">
                              <MdLink className="flex-none" /> {sale.transaction.title}
                            </span>
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">ID: {sale.transaction_id}</span>
                                                </div>
                                            ) : (
                                                <span className="text-sm text-gray-400">Bağlantı Yok</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobil Kart Görünümü */}
                        <div className="grid grid-cols-1 md:hidden gap-4">
                            {currentPageItems.map(sale => (
                                <div key={sale.id} className="flex flex-col p-4 rounded-[20px] border border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/30">
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center gap-3 w-[70%]">
                                            {/* YENİ: Mobil kartlardaki yeşil ikon arka plan düzeltildi */}
                                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-500 dark:bg-green-500/10 dark:text-green-400 font-bold flex-none">
                                                <MdShoppingCart />
                                            </div>
                                            <div className="overflow-hidden">
                                                <h6 className="text-sm font-bold text-navy-700 dark:text-white truncate">{sale.product?.name || 'Silinmiş Ürün'}</h6>
                                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 d-block mt-0.5">{formatDateTime(sale.created_at)}</span>
                                            </div>
                                        </div>
                                        {/* YENİ: Fiyat kısmı yeşil tonlaması düzeltildi */}
                                        <span className="text-base font-black text-green-600 bg-green-50 dark:bg-green-500/10 dark:text-green-400 px-2 py-0.5 rounded-md flex-none">
                      +{formatCurrency(sale.total_price)}
                    </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                        <div className="p-2 rounded-xl bg-white dark:bg-navy-800 border border-gray-100 dark:border-white/5">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Satılan Adet</span>
                                            <span className="text-sm font-black text-navy-700 dark:text-white">{sale.quantity_sold}</span>
                                        </div>
                                        <div className="p-2 rounded-xl bg-white dark:bg-navy-800 border border-gray-100 dark:border-white/5 overflow-hidden">
                                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">İlişkili İşlem</span>
                                            {/* YENİ: Mobil kartlardaki bağlantı ikonu mavi tona geçirildi */}
                                            <span className="text-xs font-bold text-blue-500 dark:text-blue-400 truncate flex items-center gap-1">
                        <MdLink className="flex-none" /> {sale.transaction?.title || 'N/A'}
                      </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* SAYFALAMA (PAGINATION) */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100 dark:border-white/10">
                                <p className="text-sm font-medium text-gray-500">
                                    <span className="font-bold text-navy-700 dark:text-white">{filteredSales.length}</span> kayıttan <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, filteredSales.length)}</span> gösteriliyor.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                    >
                                        <MdKeyboardArrowLeft className="text-xl" />
                                    </button>
                                    <div className="flex h-9 min-w-[36px] items-center justify-center rounded-lg bg-brand-500 text-white text-sm font-bold shadow-md px-2">
                                        {currentPage}
                                    </div>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                    >
                                        <MdKeyboardArrowRight className="text-xl" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default AdminStockSalesHistory;